import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, IconButton, InputAdornment,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { userActions } from '../../store';
import { roleColors, getAvatarColor } from '../../theme/tokens';

export const Users = () => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users.users);
  const [openCreate, setOpenCreate] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'member' });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    dispatch(userActions.getUsers());
  }, [dispatch]);

  const handleCreate = () => {
    dispatch(userActions.createUser(formData));
    setOpenCreate(false);
    setFormData({ name: '', email: '', password: '', role: 'member' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>Users</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Manage system users</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setOpenCreate(true)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Add User
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(users || []).map((user) => (
              <TableRow key={user._id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: getAvatarColor(user._id) }}>
                      {user.name?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{user.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{user.email}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    size="small"
                    sx={{
                      backgroundColor: roleColors[user.role]?.bg,
                      color: roleColors[user.role]?.color,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.status || 'active'}
                    size="small"
                    sx={{
                      backgroundColor: user.status === 'inactive' ? 'error.light' : 'success.light',
                      color: user.status === 'inactive' ? 'error.main' : 'success.main',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
            {(!users || users.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>No users yet</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Name" value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal" required autoFocus
          />
          <TextField
            fullWidth label="Email" type="email" value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal" required
          />
          <TextField
            fullWidth label="Password" type={showPassword ? 'text' : 'password'} value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            margin="normal" required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenCreate(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" onClick={handleCreate}
            disabled={!formData.name || !formData.email || !formData.password}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
